import { axiosClient } from "@/shared/api/axios-client";
import { encolarDescarga } from "@/shared/api/descarga-secuencial";
import type {
  CategoriaDocumento,
  DocumentoApi,
} from "@/lib/types";

/**
 * Endpoints del paso "Documentos" · subir / eliminar / descargar.
 */
/** Tamaño máximo por archivo (ajuste Documentos:TamanoMaximoMB · default 50 MB). */
export interface LimitesDocumentos {
  tamanoMaximoMb: number;
  tamanoMaximoBytes: number;
}

/** Gestión Documental · una versión de un documento (la vigente incluida). */
export interface VersionDeDocumento {
  numero: number;
  nombreOriginal: string;
  extension: string;
  tamanoBytes: number;
  hashSha256: string | null;
  subidoPorUsuarioId: string;
  subidoPorNombre: string | null;
  cargadoAt: string;
  motivo: string | null;
  esVigente: boolean;
  /** Fase 4 · fecha de emisión capturada con la versión (yyyy-MM-dd). */
  fechaEmision?: string | null;
}

/** Gestión Documental (fase 2) · un requisito del expediente (por CFDI, uno por cada CFDI). */
export interface TarjetaRequisito {
  requisitoId: string;
  categoria: string;
  categoriaNombre: string;
  categoriaDescripcion: string | null;
  /** La registra el servidor (factura del CFDI / Invoice) · no se sube aquí. */
  esDelServidor: boolean;
  extensiones: string[];
  requiereTipo: boolean;
  /** Tipo exigido por el requisito (null = se elige de `tiposPermitidos`). */
  tipoDocumento: string | null;
  tiposPermitidos: string[];
  minimo: number;
  maximo: number | null;
  porCfdi: boolean;
  numeroCfdi: number | null;
  cargados: number;
  cumple: boolean;
  puedeAgregar: boolean;
  /** Fase 4 · el documento de la tarjeta (su tipo fijo o la categoría) pide fecha de emisión. */
  manejaVigencia?: boolean;
  /** Fase 4 · de `tiposPermitidos`, los que piden fecha de emisión. */
  tiposConVigencia?: string[] | null;
}

export interface RequisitosDeSolicitud {
  completo: boolean;
  numeroCfdis: number;
  tarjetas: TarjetaRequisito[];
  faltantes: string[];
}

/** Archivo extraído de un ZIP de documentación adicional (área temporal del servidor). */
export interface ArchivoDeZip {
  id: string;
  nombre: string;
  tamanoBytes: number;
}

/** Resultado de subir un ZIP · el servidor lo descomprime y valida cada archivo. */
export interface ZipAdicional {
  loteId: string;
  archivos: ArchivoDeZip[];
  rechazados: { nombre: string; motivo: string }[];
}

export const documentosApi = {
  /** Requisitos del tipo/subtipo de la solicitud evaluados contra su expediente (arman el paso). */
  requisitos: (solicitudId: string) =>
    axiosClient.get<RequisitosDeSolicitud>(`/api/solicitudes/${solicitudId}/expediente/requisitos`).then((r) => r.data),

  limites: () => axiosClient.get<LimitesDocumentos>("/api/documentos/limites").then((r) => r.data),

  /** Documentos de la solicitud (Gestión Documental · fuente única). */
  listar: (solicitudId: string) =>
    axiosClient.get<DocumentoApi[]>(`/api/solicitudes/${solicitudId}/documentos`).then((r) => r.data),

  subir: (
    solicitudId: string,
    numeroCfdi: number,
    categoria: CategoriaDocumento,
    file: File,
    /** Tipo del catálogo (P31) · obligatorio cuando categoria === "Adicional". */
    tipoDocumento?: string,
    /** Fase 4 · fecha de emisión (yyyy-MM-dd) · obligatoria si el documento maneja vigencia. */
    fechaEmision?: string
  ) => {
    const form = new FormData();
    form.append("archivo", file);
    form.append("categoria", categoria);
    form.append("numeroCfdi", String(numeroCfdi));
    if (tipoDocumento) form.append("tipoDocumento", tipoDocumento);
    if (fechaEmision) form.append("fechaEmision", fechaEmision);
    return axiosClient
      .post<DocumentoApi>(`/api/solicitudes/${solicitudId}/documentos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /** Sube un ZIP de documentación adicional · el SERVIDOR lo descomprime y valida. */
  extraerZipAdicional: (solicitudId: string, zip: File) => {
    const form = new FormData();
    form.append("zip", zip);
    return axiosClient
      .post<ZipAdicional>(`/api/solicitudes/${solicitudId}/documentos/zip-adicional`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /** Registra como documento un archivo extraído de un ZIP, con su tipo. */
  subirArchivoDeZip: (
    solicitudId: string, loteId: string, archivoId: string, numeroCfdi: number, tipoDocumento: string, fechaEmision?: string
  ) =>
    axiosClient
      .post<DocumentoApi>(`/api/solicitudes/${solicitudId}/documentos/zip-adicional/${loteId}/${archivoId}`, {
        numeroCfdi,
        tipoDocumento,
        fechaEmision: fechaEmision || null,
      })
      .then((r) => r.data),

  /** Quita de la lista un archivo extraído de un ZIP (se borra del área temporal). */
  descartarArchivoDeZip: (solicitudId: string, loteId: string, archivoId: string) =>
    axiosClient.delete(`/api/solicitudes/${solicitudId}/documentos/zip-adicional/${loteId}/${archivoId}`),

  eliminar: (solicitudId: string, documentoId: string, motivo?: string) => {
    const url =
      `/api/solicitudes/${solicitudId}/documentos/${documentoId}` +
      (motivo ? `?motivo=${encodeURIComponent(motivo)}` : "");
    return axiosClient.delete(url).then(() => undefined);
  },

  /** Gestión Documental · reemplaza el documento creando una versión nueva (motivo obligatorio). */
  reemplazar: (solicitudId: string, documentoId: string, file: File, motivo: string, fechaEmision?: string) => {
    const form = new FormData();
    form.append("archivo", file);
    form.append("motivo", motivo);
    if (fechaEmision) form.append("fechaEmision", fechaEmision);
    return axiosClient
      .post<DocumentoApi>(`/api/solicitudes/${solicitudId}/documentos/${documentoId}/versiones`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /** Gestión Documental · historial de versiones (la más reciente primero). */
  versiones: (solicitudId: string, documentoId: string) =>
    axiosClient
      .get<VersionDeDocumento[]>(`/api/solicitudes/${solicitudId}/documentos/${documentoId}/versiones`)
      .then((r) => r.data),

  descargar: async (
    solicitudId: string,
    documentoId: string,
    nombre: string,
    /** Versión a descargar · sin ella, la vigente. */
    version?: number
  ): Promise<void> => {
    // SECUENCIAL por directiva: de una en una, sin paralelas ni ráfagas.
    const resp = await encolarDescarga(() =>
      axiosClient.get<Blob>(
        `/api/solicitudes/${solicitudId}/documentos/${documentoId}/descargar`,
        { responseType: "blob", params: version ? { version } : undefined }
      )
    );
    const url = window.URL.createObjectURL(resp.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = nombre;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Trae el blob del documento + reconstruye el blob URL con el
   * contentType correcto para que iframe/img lo interpreten bien.
   * Devuelve el `blob` original (no consumido) por si el caller quiere
   * leer texto con `blob.text()` para preview de XML/JSON.
   *
   * El caller es dueño de hacer `URL.revokeObjectURL(blobUrl)` cuando
   * termine para liberar memoria del navegador.
   */
  previsualizar: async (
    solicitudId: string,
    documentoId: string
  ): Promise<{ blobUrl: string; contentType: string; blob: Blob }> => {
    // SECUENCIAL por directiva: de una en una, sin paralelas ni ráfagas.
    const resp = await encolarDescarga(() =>
      axiosClient.get<Blob>(
        `/api/solicitudes/${solicitudId}/documentos/${documentoId}/descargar`,
        { responseType: "blob" }
      )
    );
    const contentType =
      String(resp.headers["content-type"] ?? "").split(";")[0].trim() ||
      resp.data.type ||
      "application/octet-stream";
    const blob = new Blob([resp.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);
    return { blobUrl, contentType, blob };
  },
};
